import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Por que este atendimento está voltando.
//
// A empresa cadastra os motivos em Configurações › Atendimento. Enquanto não
// houver nenhum cadastrado, reabrir continua sendo um clique só — o modal se
// tira do caminho em vez de exigir uma escolha que ninguém definiu. É o
// administrador quem decide se essa pergunta existe.
// Uma requisição só por sessão, compartilhada entre o botão (que precisa
// saber se a pergunta existe) e o modal (que precisa da lista). Sem o cache,
// cada reabertura pedia a mesma lista duas vezes.
let promessaDosMotivos = null;

export const limparCacheDeMotivos = () => {
	promessaDosMotivos = null;
};

export const carregarMotivosDeReabertura = () => {
	if (!promessaDosMotivos) {
		promessaDosMotivos = api
			.get("/reopen-reasons")
			.then(({ data }) => (Array.isArray(data) ? data : []))
			// Sem resposta, reabrir não pode travar: o motivo é registro, não
			// guarda. Lista vazia = reabre direto, como sempre foi.
			.catch(() => []);
	}

	return promessaDosMotivos;
};

const ReabrirTicketModal = ({ open, onClose, ticket, aoReabrir }) => {
	const [motivos, setMotivos] = useState([]);
	const [escolhido, setEscolhido] = useState(null);
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (!open) return;

		setEscolhido(null);
		carregarMotivosDeReabertura().then(setMotivos);
	}, [open]);

	const reabrir = async () => {
		setSalvando(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: "open",
				userId: ticket.userId,
				reopenReasonId: escolhido || undefined,
			});

			toast.success("Atendimento reaberto.");

			if (aoReabrir) aoReabrir();
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Reabrir atendimento</DialogTitle>
					<DialogDescription>
						{ticket?.contact?.name
							? `Conversa com ${ticket.contact.name}.`
							: "O atendimento volta para a lista."}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-1.5">
					<Label>Por que está reabrindo?</Label>
					<div className="grid gap-2 sm:grid-cols-2">
						{motivos.map((m) => (
							<button
								key={m.id}
								type="button"
								onClick={() => setEscolhido(m.id)}
								className={cn(
									"rounded-lg border p-2.5 text-left text-sm transition-colors",
									escolhido === m.id
										? "border-primary bg-primary/5"
										: "hover:bg-muted",
								)}
							>
								{m.name}
							</button>
						))}
					</div>
					<p className="text-xs text-muted-foreground">
						Fica registrado na conversa e no relatório de reaberturas.
					</p>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={reabrir} disabled={salvando || !escolhido}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Reabrir
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ReabrirTicketModal;
